import type {
  DefaultStatus,
  ProvenanceRecord,
  ValidRange,
} from '@arcanum-hq/shared-types';

/**
 * Per-input variable status surfaced in `<VariableRow>`. The `'—'` sentinel
 * means "no defaultStatus on the provenance record" — rendered as a dim em
 * dash. Derived independently of `outOfRange` so a row can carry both.
 */
export type VariableStatus = 'OVERRIDDEN' | 'CONFIRMED' | 'ACCEPTED' | '—';

export type OutOfRange = 'below' | 'above' | 'in-range';

export interface VariableStatusResult {
  status: VariableStatus;
  outOfRange: OutOfRange;
  /**
   * True when both a non-`'—'` status AND an out-of-range warning apply —
   * the row renders dual badges (`[overridden] [⚠ above range]`).
   */
  dualBadges: boolean;
}

const STATUS_FROM_DEFAULT_STATUS: Record<
  Exclude<DefaultStatus, null>,
  Exclude<VariableStatus, '—'>
> = {
  overridden: 'OVERRIDDEN',
  confirmed: 'CONFIRMED',
  accepted: 'ACCEPTED',
};

/**
 * Pure derivation: maps a `(record, value, range?)` tuple to the visible
 * row status + out-of-range classification. Status precedence is encoded
 * by the `defaultStatus` schema enum:
 *   `OVERRIDDEN` > `CONFIRMED` > `ACCEPTED` > `—`
 *
 * Out-of-range fires only when:
 * - `range` is provided AND
 * - `value` is a finite number AND
 * - `range.min` is set and `value < range.min`, OR `range.max` is set and
 *   `value > range.max`
 *
 * Boundary semantics: strict less-than / strict greater-than. `value === min`
 * and `value === max` are in-range. `softMin` / `softMax` are intentionally
 * NOT consumed here — the soft band is reserved for a future "near limit"
 * advisory surface; out-of-range is hard-bound only (consumer-aligned with
 * Session 1's `<ThresholdBand>` boundary semantics).
 */
export function deriveVariableStatus(
  record: ProvenanceRecord | null | undefined,
  value: unknown,
  range?: ValidRange,
): VariableStatusResult {
  const defaultStatus = record?.defaultStatus ?? null;
  const status: VariableStatus = defaultStatus
    ? STATUS_FROM_DEFAULT_STATUS[defaultStatus]
    : '—';

  let outOfRange: OutOfRange = 'in-range';
  if (range && typeof value === 'number' && Number.isFinite(value)) {
    if (range.min !== undefined && value < range.min) outOfRange = 'below';
    else if (range.max !== undefined && value > range.max) outOfRange = 'above';
  }

  return {
    status,
    outOfRange,
    dualBadges: status !== '—' && outOfRange !== 'in-range',
  };
}
